import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Grid,
  CircularProgress,
  Avatar,
  Autocomplete,
} from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import { Close, Pets } from "@mui/icons-material";
import { IPetForBackend, IUser } from "../types";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  createPet,
  createUser,
  searchUsers,
  uploadImage,
  getPets,
  fetchCep,
} from "../services";
import {
  formatPhone,
  formatCpfCnpj,
  unformatCpfCnpj,
  formatCep,
} from "../utils";

interface ModalAddPetProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  tutorId?: string | undefined;
}

const speciesOptions = [
  "Cachorro",
  "Gato",
  "Pássaro",
  "Peixe",
  "Hamster",
  "Coelho",
  "Tartaruga",
  "Porquinho-da-índia",
  "Furão",
  "Iguana",
  "Cobra",
  "Lagarto",
  "Pássaro Exótico",
  "Porco",
  "Cavalo",
  "Vaca",
  "Cabra",
  "Ovelha",
  "Galinha",
  "Outro",
];

const PetModal = (props: ModalAddPetProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [petData, setPetData] = useState<IPetForBackend>({
    name: "",
    gender: "Indefinido", // Valor padrão válido para evitar undefined
    breed: "",
    age: "",
    foto: "",
    photo: "",
    weight: "",
    species: "Cachorro", // Valor padrão válido para evitar undefined
    isDeceased: false,
    deathDate: "",
    dateOfPurchase: "",
    tutor: props.tutorId ?? "",
    dateOfBirth: "",
    color: "",
  });
  const [tutorData, setTutorData] = useState<IUser | null>(null);
  const [newTutorData, setNewTutorData] = useState({
    name: "",
    cpf: "",
    birthDate: "",
    telefone: "",
    telefonesSecundarios: "",
    celular: "",
    password: "",
    logradouro: "",
    uf: "",
    bairro: "",
    rg: "",
    cep: "",
    cidade: "",
    logradouroNumero: "",
    email: "",
    login: "",
    tecnico: false,
    idERP: "",
  });
  const [avatarSrc, setAvatarSrc] = React.useState<string | undefined>(
    undefined
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newTutor, setNewTutor] = useState<boolean>(false);
  const [users, setUsers] = useState<IUser[]>([]);
  // Estado para o Autocomplete de tutores
  const [tutorInputValue, setTutorInputValue] = useState<string>("");
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [loadingCep, setLoadingCep] = useState<boolean>(false);

  // Função debounce para otimizar as requisições
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Função de busca de usuários otimizada
  const searchUsersDebounced = React.useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (searchTerm.length < 2) {
          setUsers([]);
          setLoadingUsers(false);
          return;
        }

        setLoadingUsers(true);
        try {
          const usersData = await searchUsers(searchTerm, 500, 0);
          setUsers(usersData || []);
        } catch (error) {
          setUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      }, 300),
    []
  );

  // Função para buscar CEP automaticamente
  const handleCepSearch = async (cep: string) => {
    // Remove caracteres não numéricos
    const cepNumeros = cep.replace(/\D/g, "");

    // Verifica se o CEP tem 8 dígitos
    if (cepNumeros.length !== 8) {
      return;
    }

    setLoadingCep(true);
    try {
      const cepData = await fetchCep(cepNumeros);
      if (cepData) {
        // Preenche os campos automaticamente
        setNewTutorData((prev) => ({
          ...prev,
          logradouro: cepData.Logradouro || "",
          bairro: cepData.Bairro || "",
          cidade: cepData.MunicipioNome || "",
          uf: cepData.EstadoUF || "",
        }));
      }
    } catch (error) {
    } finally {
      setLoadingCep(false);
    }
  };

  React.useEffect(() => {
    // Atualiza apenas os campos que dependem das props, mantendo o estado existente
    setPetData((prev) => ({
      ...prev,
      tutor: props.tutorId ?? "",
    }));
    // Não carrega mais todos os usuários inicialmente
    // A busca é feita on-demand conforme o usuário digita
    setUsers([]);
  }, [props.tutorId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPetData((prev) => {
      if (name === "specie" || name === "species") {
        return { ...prev, species: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleInputChangeUser = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Aplica formatação específica dependendo do campo
    if (name === "cpf") {
      formattedValue = formatCpfCnpj(value);
    } else if (name === "telefone" || name === "celular") {
      formattedValue = formatPhone(value);
    } else if (name === "cep") {
      formattedValue = formatCep(value);
      // Busca automaticamente o CEP quando completar 8 dígitos
      const cepNumeros = value.replace(/\D/g, "");
      if (cepNumeros.length === 8) {
        handleCepSearch(cepNumeros);
      }
    } else if (name === "uf") {
      // Converte para maiúsculas e limita a 2 caracteres
      formattedValue = value.toUpperCase().substring(0, 2);
    }

    setNewTutorData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const onCloseButton = () => {
    props.onClose();
    // Reinicializa com valores válidos em vez de null para evitar componentes não-controlados
    setPetData({
      name: "",
      gender: "Indefinido",
      breed: "",
      age: "",
      foto: "",
      photo: "",
      weight: "",
      species: "Cachorro",
      isDeceased: false,
      deathDate: "",
      dateOfPurchase: "",
      tutor: props.tutorId ?? "",
      dateOfBirth: "",
      color: "",
    });
    setAvatarSrc(undefined);
    setNewTutorData({
      name: "",
      cpf: "",
      birthDate: "",
      telefone: "",
      telefonesSecundarios: "",
      celular: "",
      password: "",
      logradouro: "",
      bairro: "",
      cep: "",
      uf: "",
      rg: "",
      cidade: "",
      logradouroNumero: "",
      email: "",
      login: "",
      tecnico: false,
      idERP: "",
    });
    setNewTutor(false);
    setTutorData(null);
    setTutorInputValue("");
    setError(false);
    setErrorMessage("");
    setIsSubmitting(false);
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setPetData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChangeTutor = (event: any, newValue: IUser | null) => {
    setTutorData(newValue);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(false);

    try {
      // Validação básica dos campos obrigatórios do pet
      if (!petData.name || petData.name.trim() === "") {
        setErrorMessage("Nome do pet é obrigatório");
        setError(true);
        setIsSubmitting(false);
        return;
      }

      let imageUrl = "";
      let finalTutorId = "";

      // Determinar qual tutor usar
      if (props.tutorId) {
        // Caso 1: tutorId foi passado como prop (editando pet existente)
        finalTutorId = props.tutorId;
      } else if (newTutor) {
        // Caso 2: Criando um novo tutor
        if (
          !newTutorData.name ||
          (!newTutorData.telefone && !newTutorData.celular)
        ) {
          setErrorMessage(
            "Nome e pelo menos um telefone (fixo ou celular) são obrigatórios para novo tutor"
          );
          setError(true);
          return;
        }

        // Mapeia os campos do frontend para o formato esperado pelo backend
        const formattedData = {
          nome: newTutorData.name,
          cpf: newTutorData.cpf ? unformatCpfCnpj(newTutorData.cpf) : "",
          telefone: newTutorData.telefone
            ? formatPhone(newTutorData.telefone)
            : "",
          email: newTutorData.email,
          dataNascimento: newTutorData.birthDate
            ? `${newTutorData.birthDate}T12:00:00`
            : "",
          endereco: newTutorData.logradouro || "",
          cidade: newTutorData.cidade || "",
          estado: newTutorData.uf || "",
          cep: newTutorData.cep || "",
          // Campos opcionais
          celular: newTutorData.celular
            ? formatPhone(newTutorData.celular)
            : newTutorData.telefone
              ? formatPhone(newTutorData.telefone)
              : "",
          rg: newTutorData.rg || "",
          bairro: newTutorData.bairro || "",
          logradouroNumero: newTutorData.logradouroNumero || "",
          // CORREÇÃO: Incluir o idERP para que seja mapeado corretamente no backend
          idERP: newTutorData.idERP || "",
          login:
            newTutorData.cpf || newTutorData.email || newTutorData.telefone, // Login baseado no CPF, email ou telefone
        };

        const createdUserResponse = await createUser(formattedData);
        finalTutorId = createdUserResponse || "";
      } else if (tutorData) {
        // Caso 3: Selecionou um tutor existente
        finalTutorId = tutorData.ID || "";
      }

      // Validação final do tutor
      if (!finalTutorId) {
        setErrorMessage("Selecione um tutor ou cadastre um novo");
        setError(true);
        return;
      }

      // ✅ VALIDAÇÃO DE PET DUPLICADO: Verificar se já existe um pet com o mesmo nome e tutor
      try {
        const allPets = await getPets();
        const duplicatePet = allPets?.find((existingPet: any) => {
          // Comparar nome (case insensitive) e tutor
          const sameNameIgnoreCase =
            existingPet.name?.toLowerCase().trim() ===
            petData.name.toLowerCase().trim();

          // Verificar se é o mesmo tutor (pode ser ID local UUID ou ID da API externa)
          const sameTutor =
            existingPet.tutor?.id === finalTutorId ||
            existingPet.ownerId === finalTutorId ||
            existingPet.tutor?.ID === finalTutorId;

          return sameNameIgnoreCase && sameTutor;
        });

        if (duplicatePet) {
          const tutorName =
            duplicatePet.tutor?.name ||
            duplicatePet.tutor?.NomeFantasia ||
            "Tutor não informado";
          setErrorMessage(
            `Já existe um pet com o nome "${petData.name}" cadastrado para o tutor "${tutorName}"`
          );
          setError(true);
          return;
        }
      } catch (petValidationError) {
        // Continua com a criação mesmo se a validação falhar, mas loga o erro
      }

      if (selectedFile) {
        imageUrl = await uploadImage(selectedFile);
      }

      const dataToSend: IPetForBackend = {
        name: petData.name,
        gender: petData.gender,
        breed: petData.breed,
        foto: imageUrl,
        weight: petData.weight,
        species: petData.species,
        isDeceased: false,
        deathDate: petData.deathDate
          ? new Date(petData.deathDate).toISOString().slice(0, 10)
          : "",
        tutor: finalTutorId,
        tutorData: tutorData || undefined, // Inclui dados completos do tutor para sincronização
        dateOfBirth: petData.dateOfBirth
          ? new Date(petData.dateOfBirth).toISOString().slice(0, 10)
          : "",
        dateOfPurchase: petData.dateOfPurchase
          ? new Date(petData.dateOfPurchase).toISOString().slice(0, 10)
          : "",
        color: petData.color,
      };
      await createPet(dataToSend);

      if (props.onSave) props.onSave();
      onCloseButton();
    } catch (error) {
      setError(true);

      // Tenta extrair uma mensagem de erro mais específica
      const errorMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        "Erro ao salvar pet. Por favor, tente novamente.";

      setErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={onCloseButton}
      aria-labelledby="pet-modal-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: isMobile ? "100%" : 600,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography id="pet-modal-title" variant="h6" component="h2">
            Cadastrar Novo Pet
          </Typography>
          <IconButton
            onClick={() => {
              props.onClose();
              setAvatarSrc(undefined);
            }}
            disabled={isSubmitting}
          >
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: "center" }}>
            <ButtonBase
              component="label"
              role={undefined}
              tabIndex={-1} // prevent label from tab focus
              aria-label="Avatar image"
              sx={{
                borderRadius: "40px",
                "&:has(:focus-visible)": {
                  outline: "2px solid",
                  outlineOffset: "2px",
                },
              }}
            >
              <Avatar
                alt="Upload new avatar"
                src={avatarSrc || petData?.photo || petData?.foto}
                sx={{ width: 100, height: 100 }}
              >
                {!avatarSrc && !petData?.photo && !petData?.foto && <Pets />}
              </Avatar>
              <input
                type="file"
                accept="image/*"
                style={{
                  border: 0,
                  clip: "rect(0 0 0 0)",
                  height: "1px",
                  margin: "-1px",
                  overflow: "hidden",
                  padding: 0,
                  position: "absolute",
                  whiteSpace: "nowrap",
                  width: "1px",
                }}
                onChange={handleAvatarChange}
              />
            </ButtonBase>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Nome do Pet"
              name="name"
              value={petData.name || ""}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={isSubmitting}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Sexo</InputLabel>
              <Select
                name="gender"
                value={petData.gender || "Indefinido"}
                onChange={handleSelectChange}
                label="Sexo"
                required
                disabled={isSubmitting}
              >
                <MenuItem value="Fêmea">Fêmea</MenuItem>
                <MenuItem value="Macho">Macho</MenuItem>
                <MenuItem value="Indefinido">Indefinido</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Raça"
              name="breed"
              value={petData.breed || ""}
              onChange={handleInputChange}
              margin="normal"
              required
              disabled={isSubmitting}
            />
          </Grid>

          {/* <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Idade (anos)"
              name="age"
              type="number"
              value={petData?.age}
              onChange={handleInputChange}
              margin="normal"
              inputProps={{ min: 0 }}
              disabled={isSubmitting}
            />
          </Grid> */}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Peso (kg)"
              name="weight"
              type="number"
              value={petData.weight || ""}
              onChange={handleInputChange}
              margin="normal"
              inputProps={{ min: 0, step: "0.1" }}
              disabled={isSubmitting}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Espécie</InputLabel>
              <Select
                name="species"
                value={petData.species || "Cachorro"}
                onChange={handleSelectChange}
                label="Espécie"
                required
                disabled={isSubmitting}
              >
                {speciesOptions.map((species) => (
                  <MenuItem key={species} value={species}>
                    {species}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Cor"
              name="color"
              value={petData.color || ""}
              onChange={handleInputChange}
              margin="normal"
              disabled={isSubmitting}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Data de Nascimento"
              name="dateOfBirth"
              type="date"
              value={petData.dateOfBirth || ""}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Data da compra"
              name="dateOfPurchase"
              type="date"
              value={petData.dateOfPurchase || ""}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
            />
          </Grid>

          {!props?.tutorId && !newTutor && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={users}
                loading={loadingUsers}
                getOptionLabel={(option) => option.NomeFantasia || ""}
                value={tutorData}
                onChange={handleSelectChangeTutor}
                inputValue={tutorInputValue}
                onInputChange={(event, newInputValue) => {
                  setTutorInputValue(newInputValue);
                  // Chama a busca com debounce
                  searchUsersDebounced(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vincular Tutor"
                    margin="normal"
                    disabled={isSubmitting}
                    placeholder="Digite o nome do tutor para buscar..."
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.ID}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body1">
                        {option.NomeFantasia}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        CPF/CNPJ: {option.CNPJ_CPF}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  tutorInputValue.length < 2
                    ? "Digite ao menos 2 caracteres para buscar"
                    : "Nenhum tutor encontrado"
                }
                isOptionEqualToValue={(option, value) =>
                  option.ID === value?.ID
                }
                sx={{ mt: 1 }}
              />
              <Button
                variant="outlined"
                onClick={() => setNewTutor(true)}
                sx={{ mt: 1, width: "100%" }}
                disabled={isSubmitting}
              >
                Cadastrar novo tutor
              </Button>
            </Grid>
          )}
        </Grid>

        {newTutor && (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, mb: 1, fontWeight: "medium" }}
              >
                Dados do Tutor
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nome do Tutor"
                name="name"
                value={newTutorData.name || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                required
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="CPF/CNPJ"
                name="cpf"
                fullWidth
                value={newTutorData.cpf || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefone"
                name="telefone"
                value={newTutorData.telefone || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
                error={!newTutorData.telefone && !newTutorData.celular}
                helperText={
                  !newTutorData.telefone && !newTutorData.celular
                    ? "Telefone ou celular obrigatório"
                    : ""
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Celular"
                name="celular"
                value={newTutorData.celular || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
                error={!newTutorData.telefone && !newTutorData.celular}
                helperText={
                  !newTutorData.telefone && !newTutorData.celular
                    ? "Telefone ou celular obrigatório"
                    : ""
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={newTutorData.email || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: "medium" }}
              >
                Endereço
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="CEP"
                name="cep"
                value={newTutorData.cep || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting || loadingCep}
                placeholder="00000-000"
                InputProps={{
                  endAdornment: loadingCep ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
                helperText={
                  loadingCep
                    ? "Buscando endereço..."
                    : "Digite o CEP para preencher automaticamente"
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Logradouro (Rua/Av.)"
                name="logradouro"
                value={newTutorData.logradouro || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Número"
                name="logradouroNumero"
                value={newTutorData.logradouroNumero || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Bairro"
                name="bairro"
                value={newTutorData.bairro || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Cidade"
                name="cidade"
                value={newTutorData.cidade || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="UF (Estado)"
                name="uf"
                value={newTutorData.uf || ""}
                onChange={handleInputChangeUser}
                margin="normal"
                disabled={isSubmitting}
                placeholder="PR"
                inputProps={{
                  maxLength: 2,
                  style: { textTransform: "uppercase" },
                }}
              />
            </Grid>
          </Grid>
        )}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onCloseButton}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !petData.name ||
              !petData.gender ||
              !petData.breed ||
              (!props.tutorId && !tutorData && !newTutor) ||
              !petData.species
            }
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PetModal;
